import React, { useState, useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled } from '@mui/material/styles';
import { CommentHistory, InstallationHistory } from '../definitions/sensor';
import { MOBILE_MEDIA_QUERY_WIDTH } from '../utils/constants';
import { formatDateOnlyToCDT } from '../utils/helpers';
import theme from '../theme';
import { Avatar, Box, Button, Chip, Paper, Typography, Divider } from '@mui/material';
import AddCommentIcon from '@mui/icons-material/AddComment';
import CommentsForm from './CommentsForm';

const CommentsContainer = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const CommentHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
});

const CommentItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: 'none',
  border: 'none',
  '&:last-child': {
    marginBottom: 0,
  },
}));

const CommentAuthor = styled(Typography)({
  fontWeight: 'bold',
  marginRight: theme.spacing(1),
});

const CommentDate = styled(Typography)({
  color: theme.palette.text.secondary,
  fontSize: '0.8rem',
});

const CommentBody = styled(Typography)({
  fontFamily: 'sans-serif',
  color: theme.palette.text.primary,
  lineHeight: 1.5,
  overflowWrap: 'break-word',
});

const AddCommentBtn = styled(Button)(() => ({
  backgroundColor: '#f27141',
  color: '#fff',
  borderRadius: 20,
  padding: '8px 16px',
  '&:hover': {
    background: '#e06b3e',
  },
}));

const getAvatarInitial = (name: string) => {
  return name ? name.charAt(0).toUpperCase() : '';
};

export default function CommentsHistoryTable({
  commentHistory
}: {
  commentHistory: CommentHistory[],
}) {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY_WIDTH);
  const [openAddCommentDialog, setOpenAddCommentDialog] = useState(false);

  const handleOpenAddCommentDialog = () => setOpenAddCommentDialog(true);
  const handleCloseAddCommentDialog = () => setOpenAddCommentDialog(false);

  return (
    <CommentsContainer sx={{ backgroundColor: '#f9f9f9' }}>
      <CommentHeader>
        <Typography variant="h6">General Comments ({commentHistory.length})</Typography>
        <AddCommentBtn startIcon={<AddCommentIcon />} onClick={handleOpenAddCommentDialog}>
          Add General comment
        </AddCommentBtn>
      </CommentHeader>

      <CommentsForm open={openAddCommentDialog} handleClose={handleCloseAddCommentDialog} title="Add General Comment" />

      <Box>
        {commentHistory.map(({ comment, createdOn, createdBy }: CommentHistory, index) => (
          <React.Fragment key={createdBy + createdOn + index}>
            <CommentItem>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(1), flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 30, height: 30, fontSize: '0.8rem', marginRight: theme.spacing(1) }}>
                    {getAvatarInitial(createdBy)}
                  </Avatar>
                  <CommentAuthor variant="subtitle2">{createdBy}</CommentAuthor>

                </Box>
                <CommentDate variant="caption">{formatDateOnlyToCDT(createdOn)}</CommentDate>

              </Box>
              <CommentBody variant="body2">{comment}</CommentBody>
            </CommentItem>
            {index < commentHistory.length - 1 && <Divider sx={{ my: 1, borderColor: 'rgba(0, 0, 0, 0.1)' }} />}
          </React.Fragment>
        ))}
      </Box >
    </CommentsContainer >
  );
};
